import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { User, SessionTokens } from './types';
import { saveTokens, loadTokens, clearTokens } from './types';
import { supabase } from '@bright-web-skeleton/supabase';
import {
  resolvePlatformLocaleFromMetadata,
  type SupportedPlatformLocale,
} from '@bright-web-skeleton/shared';
import { updateAuthenticatedPlatformLocale } from './platformLanguageAuth';
import {
  getAppUserIdForAuthUser,
  getAppProfile,
  linkAuthUserToApp,
  type AppId,
} from './services/appUserService';

// Session expired storage key - consistent across apps
const SESSION_EXPIRED_KEY = 'bright_session_expired_reason';

/**
 * ============================================================================
 * AUTH INVARIANT (MONOREPO-WIDE CONTRACT)
 * ============================================================================
 *
 * 1. Auth initializes ONCE per page load
 *    - Supabase may emit multiple events (INITIAL_SESSION, SIGNED_IN, etc.)
 *    - AuthProvider normalizes these into a single initialization
 *    - authInitializedRef guards against duplicate execution
 *
 * 2. authReady is the ONLY signal apps may depend on for effects
 *    - Set to true exactly once after initialization completes
 *    - NEVER toggles back to false
 *    - All downstream effects MUST gate on authReady
 *
 * 3. user/session/authUser/profile MUST NOT trigger effects
 *    - These values may change during auth event churn
 *    - Using them as effect dependencies causes duplicate API calls
 *    - ESLint enforces this via no-restricted-syntax rule
 *
 * 4. All app-level data fetching must use useRef guards
 *    - Pattern: if (!authReady || hasFetchedRef.current) return;
 *    - Ensures exactly one fetch per page load
 *
 * Violations of this invariant will cause:
 * - Duplicate API calls
 * - Race conditions
 * - Inconsistent UI state
 * - Admin action failures (delete, suspend, etc.)
 * ============================================================================
 */

/**
 * Auth state interface for the context
 */
export interface AuthState {
  authUser: User | null;
  appUserId: string | null;
  appProfile: Record<string, unknown> | null;
  /** Canonical signed-in account interface language. */
  platformLocale: SupportedPlatformLocale;
  /** Persist one explicit account preference through Supabase Auth metadata. */
  updatePlatformLocale: (locale: SupportedPlatformLocale) => Promise<void>;
  loading: boolean;
  /**
   * STABLE SIGNAL: True exactly once after auth initialization completes.
   * Never toggles back to false. Use this to gate API calls.
   */
  authReady: boolean;
  isSessionExpired: boolean;
  sessionExpiredMessage: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (redirectPath?: string) => Promise<void>;
  loginWithMagicLink: (email: string, redirectPath?: string) => Promise<void>;
  /**
   * Passwordless email sign-in (OTP). Works for both new and returning users.
   * Uses signInWithOtp with shouldCreateUser: true.
   */
  requestEmailSignIn: (
    email: string,
    redirectQuery?: string,
  ) => Promise<{ ok: boolean; error?: string }>;
  signup: (
    email: string,
    password: string,
    name?: string,
    redirectPath?: string,
  ) => Promise<{ needsEmailConfirmation: boolean }>;
  logout: () => Promise<void>;
  linkAppUser: (appUserId: string) => Promise<void>;
  clearSessionExpired: () => void;
  refreshAppProfile: () => Promise<void>;
}

/**
 * Props for AuthProvider component
 */
export interface AuthProviderProps {
  children: ReactNode;
  appId: AppId;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Hook to access auth context
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * AuthProvider component that wraps the app and provides auth context.
 * Includes robust OAuth callback handling with debounce for SIGNED_OUT race conditions.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  appId,
}): React.ReactElement => {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [appUserId, setAppUserId] = useState<string | null>(null);
  const [appProfile, setAppProfile] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // STABLE SIGNAL: set to true exactly once, never toggles back
  const [authReady, setAuthReady] = useState<boolean>(false);
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);
  const platformLocale = useMemo(
    () => resolvePlatformLocaleFromMetadata(authUser?.user_metadata),
    [authUser?.user_metadata],
  );

  // Refs for state that shouldn't trigger re-renders
  const hadPreviousSessionRef = useRef<boolean>(false);
  const initStartedRef = useRef<boolean>(false);
  const authInitializedRef = useRef<boolean>(false);
  // CRITICAL: Track if app profile has been loaded to prevent duplicate calls
  const profileLoadedRef = useRef<boolean>(false);
  const absoluteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const signOutDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Clear all pending timeouts
  const clearAllTimeouts = useCallback(() => {
    if (absoluteTimeoutRef.current) {
      clearTimeout(absoluteTimeoutRef.current);
      absoluteTimeoutRef.current = null;
    }
    if (signOutDebounceRef.current) {
      clearTimeout(signOutDebounceRef.current);
      signOutDebounceRef.current = null;
    }
  }, []);

  // Handle session expiration
  const handleSessionExpired = useCallback(
    (reason: string) => {
      console.log(`[AuthProvider:${appId}] Session expired:`, reason);
      setIsSessionExpired(true);
      setSessionExpiredMessage(reason);
      setAuthUser(null);
      setAppUserId(null);
      setAppProfile(null);
      try {
        localStorage.setItem(SESSION_EXPIRED_KEY, reason);
      } catch (e) {
        // localStorage may not be available
      }
    },
    [appId],
  );

  // Clear session expired state
  const clearSessionExpired = useCallback(() => {
    setIsSessionExpired(false);
    setSessionExpiredMessage(null);
    try {
      localStorage.removeItem(SESSION_EXPIRED_KEY);
      sessionStorage.removeItem('manual_sign_out');
    } catch (e) {
      // Storage may not be available
    }
  }, []);

  // Load app-specific profile data
  // CRITICAL: Admin app NEVER queries app_user_map - it uses admins table directly
  // IDEMPOTENT: Only runs once per auth initialization
  const loadAppProfile = useCallback(
    async (userId: string, force: boolean = false) => {
      // CRITICAL: Prevent duplicate profile loads during auth initialization
      if (profileLoadedRef.current && !force) {
        console.log(`[AuthProvider:${appId}] Profile already loaded, skipping duplicate call`);
        return;
      }
      profileLoadedRef.current = true;

      // Admin app: bypass app_user_map entirely, check admins table directly
      if (appId === 'admin') {
        console.log(`[AuthProvider:admin] Loading admin profile (once)`);
        try {
          const { data: adminData, error: adminError } = await supabase
            .from('admins')
            .select('*')
            .eq('user_id', userId)
            .maybeSingle();

          if (adminError) {
            console.warn(`[AuthProvider:admin] Error checking admins table:`, adminError);
          }

          // For admin app: appUserId = null (not app-scoped), appProfile = admin record or null
          setAppUserId(null);
          setAppProfile(adminData || null);
          console.log(
            `[AuthProvider:admin] Admin profile loaded:`,
            adminData ? 'found' : 'not found',
          );
        } catch (e) {
          console.warn(`[AuthProvider:admin] Failed to load admin profile:`, e);
          setAppUserId(null);
          setAppProfile(null);
        }
        return;
      }

      // Non-admin apps: use app_user_map as normal
      console.log(`[AuthProvider:${appId}] Loading app profile (once)`);
      try {
        const mapped = await getAppUserIdForAuthUser(userId, appId);
        setAppUserId(mapped);
        setAppProfile(mapped ? await getAppProfile(appId, mapped) : null);
      } catch (e) {
        console.warn(`[AuthProvider:${appId}] Failed to load app profile:`, e);
      }
    },
    [appId],
  );

  // Check for stored session expired message on mount
  useEffect(() => {
    try {
      const storedReason = localStorage.getItem(SESSION_EXPIRED_KEY);
      if (storedReason) {
        setIsSessionExpired(true);
        setSessionExpiredMessage(storedReason);
      }
    } catch (e) {
      // localStorage may not be available
    }
  }, []);

  // Main auth initialization effect
  useEffect(() => {
    // Guard: only run once
    if (initStartedRef.current) {
      return;
    }
    initStartedRef.current = true;

    let isMounted = true;
    console.log(`[AuthProvider:${appId}] Auth init started`);

    // Diagnostic only: classify exact auth parameter names rather than
    // substring matches such as discount_code= or error_code=.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const searchParams = new URLSearchParams(window.location.search);
    const hasOAuthTokensInHash = hashParams.has('access_token') || hashParams.has('error');
    const hasOAuthCodeInSearch = searchParams.has('code') || searchParams.has('error');
    const isOAuthCallback = hasOAuthTokensInHash || hasOAuthCodeInSearch;

    if (isOAuthCallback) {
      console.log(`[AuthProvider:${appId}] OAuth callback detected`);
    }

    // Complete initialization helper - sets authReady ONCE
    const completeInitialization = (source: string) => {
      if (isMounted && !authInitializedRef.current) {
        console.log(
          `[AuthProvider:${appId}] Auth init completed (source: ${source}) - setting authReady=true`,
        );
        clearAllTimeouts();
        authInitializedRef.current = true;
        setAuthReady(true); // STABLE SIGNAL: set once, never false again
        setLoading(false);
      }
    };

    // Handle SIGNED_OUT with debounce - wait for possible INITIAL_SESSION
    // Reduced to 300ms since we now call getSession() immediately on mount
    const handleSignedOutWithDebounce = () => {
      if (signOutDebounceRef.current) {
        clearTimeout(signOutDebounceRef.current);
      }

      signOutDebounceRef.current = setTimeout(() => {
        signOutDebounceRef.current = null;
        if (isMounted && !authInitializedRef.current) {
          console.log(`[AuthProvider:${appId}] SIGNED_OUT debounce elapsed`);
          setAuthUser(null);
          setAppUserId(null);
          setAppProfile(null);
          completeInitialization('SIGNED_OUT_DEBOUNCED');
        }
      }, 300);
    };

    // CRITICAL: Call getSession() immediately on mount BEFORE setting up listener
    // This hydrates the session from localStorage faster and prevents Chrome
    // from treating Supabase as an "intermediate website" during OAuth
    const hydrateSessionImmediately = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();
        if (error) {
          console.warn(`[AuthProvider:${appId}] Initial getSession error:`, error.message);
          return;
        }

        if (session?.user && isMounted && !authInitializedRef.current) {
          console.log(`[AuthProvider:${appId}] Session hydrated from storage:`, session.user.email);
          setAuthUser(session.user as User);
          hadPreviousSessionRef.current = true;
          saveTokens({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
            expires_at: session.expires_at,
          });
          await loadAppProfile(session.user.id);
          completeInitialization('IMMEDIATE_HYDRATION');
        }
      } catch (err) {
        console.error(`[AuthProvider:${appId}] Session hydration error:`, err);
      }
    };

    // Start immediate hydration (don't await - let listener run in parallel)
    hydrateSessionImmediately();

    // Set up absolute timeout (8 seconds - reduced since we hydrate immediately)
    absoluteTimeoutRef.current = setTimeout(async () => {
      if (isMounted && !authInitializedRef.current) {
        console.warn(
          `[AuthProvider:${appId}] Auth init TIMEOUT (8s) - attempting fallback getSession`,
        );

        try {
          const {
            data: { session: fallbackSession },
            error: sessionError,
          } = await supabase.auth.getSession();

          if (fallbackSession?.user) {
            setAuthUser(fallbackSession.user as User);
            hadPreviousSessionRef.current = true;
            await loadAppProfile(fallbackSession.user.id);
          } else {
            setAuthUser(null);
            setAppUserId(null);
            setAppProfile(null);
          }

          if (sessionError) {
            console.error(`[AuthProvider:${appId}] Fallback session error:`, sessionError.message);
          }
        } catch (err) {
          console.error(`[AuthProvider:${appId}] Fallback getSession error:`, err);
        }

        completeInitialization('ABSOLUTE_TIMEOUT');
      }
    }, 8000);

    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      // Only log significant events to reduce noise
      if (event !== 'TOKEN_REFRESHED') {
        console.log(
          `[AuthProvider:${appId}] Auth event: ${event}`,
          session?.user?.email || 'no user',
        );
      }

      // If INITIAL_SESSION has no user but we might have session in storage, check directly
      if (event === 'INITIAL_SESSION' && !session?.user && !authInitializedRef.current) {
        try {
          const {
            data: { session: directSession },
          } = await supabase.auth.getSession();
          if (directSession?.user) {
            // Clear any pending debounce
            if (signOutDebounceRef.current) {
              clearTimeout(signOutDebounceRef.current);
              signOutDebounceRef.current = null;
            }
            setAuthUser(directSession.user as User);
            hadPreviousSessionRef.current = true;
            saveTokens({
              access_token: directSession.access_token,
              refresh_token: directSession.refresh_token,
              expires_at: directSession.expires_at,
            });
            await loadAppProfile(directSession.user.id);
            completeInitialization('INITIAL_SESSION_RECOVERED');
            return;
          }
        } catch (err) {
          console.error(`[AuthProvider:${appId}] Session recovery failed:`, err);
        }
      }

      switch (event) {
        case 'INITIAL_SESSION':
          // Clear any pending SIGNED_OUT debounce
          if (signOutDebounceRef.current) {
            clearTimeout(signOutDebounceRef.current);
            signOutDebounceRef.current = null;
          }

          if (session?.user) {
            setAuthUser(session.user as User);
            hadPreviousSessionRef.current = true;
            saveTokens({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
              expires_at: session.expires_at,
            });

            // Load app profile then complete initialization
            setTimeout(async () => {
              if (isMounted) {
                await loadAppProfile(session.user.id);
                completeInitialization('INITIAL_SESSION');
              }
            }, 0);
          } else {
            setAuthUser(null);
            setAppUserId(null);
            setAppProfile(null);
            completeInitialization('INITIAL_SESSION_NO_USER');
          }
          break;

        case 'SIGNED_IN':
        case 'TOKEN_REFRESHED':
          // Clear any pending debounce
          if (signOutDebounceRef.current) {
            clearTimeout(signOutDebounceRef.current);
            signOutDebounceRef.current = null;
          }

          clearSessionExpired();

          if (session?.user) {
            setAuthUser(session.user as User);
            hadPreviousSessionRef.current = true;
            saveTokens({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
              expires_at: session.expires_at,
            });

            // Defer Supabase calls with setTimeout to prevent deadlocks
            setTimeout(async () => {
              if (isMounted) {
                await loadAppProfile(session.user.id);
                completeInitialization('SIGNED_IN');
              }
            }, 0);
          }
          break;

        case 'SIGNED_OUT': {
          // If we're still initializing, use debounce to wait for possible INITIAL_SESSION
          if (!authInitializedRef.current) {
            handleSignedOutWithDebounce();
            return;
          }

          // After initialization, handle normally
          const wasManualSignOut = (() => {
            try {
              return sessionStorage.getItem('manual_sign_out') === 'true';
            } catch {
              return false;
            }
          })();

          if (hadPreviousSessionRef.current && !session && !wasManualSignOut) {
            handleSessionExpired(
              'You were signed out because your session expired. Please sign in again.',
            );
          }

          try {
            sessionStorage.removeItem('manual_sign_out');
          } catch (e) {
            // Storage may not be available
          }

          setAuthUser(null);
          setAppUserId(null);
          setAppProfile(null);
          setLoading(false);
          break;
        }

        case 'USER_UPDATED':
          if (session?.user) {
            setAuthUser(session.user as User);
            setTimeout(async () => {
              if (isMounted) {
                await loadAppProfile(session.user.id);
              }
            }, 0);
          }
          break;

        default:
          if (session?.user) {
            setAuthUser(session.user as User);
            hadPreviousSessionRef.current = true;
            setTimeout(async () => {
              if (isMounted) {
                await loadAppProfile(session.user.id);
                completeInitialization(`default_${event}`);
              }
            }, 0);
          } else if (!authInitializedRef.current) {
            setAuthUser(null);
            setAppUserId(null);
            setAppProfile(null);
            completeInitialization(`default_${event}_no_user`);
          }
      }
    });

    // Cleanup
    return () => {
      console.log(`[AuthProvider:${appId}] Cleanup - unmounting`);
      isMounted = false;
      clearAllTimeouts();
      subscription.unsubscribe();
    };
  }, [appId, clearAllTimeouts, clearSessionExpired, handleSessionExpired, loadAppProfile]);

  // Set up visibility change listener for session refresh
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && hadPreviousSessionRef.current) {
        console.log(`[AuthProvider:${appId}] App became visible, checking session...`);
        try {
          const { data, error } = await supabase.auth.getSession();

          if (error) {
            console.error(`[AuthProvider:${appId}] Session refresh error:`, error.message);
            if (
              error.message.includes('refresh_token') ||
              error.message.includes('expired') ||
              error.message.includes('invalid') ||
              error.message.includes('JWT')
            ) {
              handleSessionExpired('Your session has expired. Please sign in again.');
            }
          } else if (!data.session && authUser) {
            handleSessionExpired('Your session has expired. Please sign in again.');
          }
        } catch (err) {
          console.error(`[AuthProvider:${appId}] Session check failed:`, err);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [appId, authUser, handleSessionExpired]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      setLoading(true);
      clearSessionExpired();

      const res = await supabase.auth.signInWithPassword({ email, password });
      if (res.error) {
        setLoading(false);
        throw res.error;
      }
      const session = res.data?.session;
      if (session) {
        saveTokens({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          expires_at: session.expires_at,
        });
      }

      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        setAuthUser(data.user as User);
        hadPreviousSessionRef.current = true;
        await loadAppProfile(data.user.id);
      }
      setLoading(false);
    },
    [clearSessionExpired, loadAppProfile],
  );

  const loginWithGoogle = useCallback(
    async (redirectPath: string = '/onboarding'): Promise<void> => {
      setLoading(true);
      clearSessionExpired();

      try {
        const redirectUrl = `${window.location.origin}${redirectPath}`;
        console.log(`[AuthProvider:${appId}] Starting Google OAuth...`);
        console.log(`[AuthProvider:${appId}] - Redirect URL:`, redirectUrl);
        console.log(
          `[AuthProvider:${appId}] - IMPORTANT: Ensure this URL is in Supabase Redirect URLs list`,
        );

        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: redirectUrl,
            scopes: 'email profile',
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            },
          },
        });
        if (error) throw error;
      } finally {
        setLoading(false);
      }
    },
    [appId, clearSessionExpired],
  );

  const loginWithMagicLink = useCallback(
    async (email: string, redirectPath: string = '/onboarding'): Promise<void> => {
      setLoading(true);
      clearSessionExpired();

      try {
        const redirectUrl = `${window.location.origin}${redirectPath}`;
        console.log(`[AuthProvider:${appId}] Sending magic link to:`, email);
        console.log(`[AuthProvider:${appId}] - Redirect URL:`, redirectUrl);

        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: redirectUrl,
          },
        });
        if (error) throw error;
      } finally {
        setLoading(false);
      }
    },
    [appId, clearSessionExpired],
  );

  const requestEmailSignIn = useCallback(
    async (email: string, redirectQuery?: string): Promise<{ ok: boolean; error?: string }> => {
      clearSessionExpired();

      try {
        // redirectQuery is an optional URL query string (without leading "?")
        // appended to /auth/callback so signals like ?role=connector survive
        // cross-browser email-link redirects.
        const suffix = redirectQuery ? `?${redirectQuery}` : '';
        const redirectUrl = `${window.location.origin}/auth/callback${suffix}`;
        console.log(`[AuthProvider:${appId}] Passwordless email sign-in for:`, email);
        console.log(`[AuthProvider:${appId}] - Redirect URL:`, redirectUrl);

        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: redirectUrl,
            shouldCreateUser: true,
          },
        });
        if (error) return { ok: false, error: error.message };
        return { ok: true };
      } catch (e: any) {
        return { ok: false, error: e.message || 'Failed to send sign-in link' };
      }
    },
    [appId, clearSessionExpired],
  );

  const signup = useCallback(
    async (
      email: string,
      password: string,
      name?: string,
      redirectPath: string = '/onboarding',
    ): Promise<{ needsEmailConfirmation: boolean }> => {
      setLoading(true);
      clearSessionExpired();

      try {
        const redirectUrl = `${window.location.origin}${redirectPath}`;
        console.log(`[AuthProvider:${appId}] Email signup redirect URL:`, redirectUrl);

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: name ? { full_name: name, name } : undefined,
            emailRedirectTo: redirectUrl,
          },
        });
        if (error) throw error;

        // When Confirm Email is ON, Supabase returns user but no session
        const needsEmailConfirmation = !!data.user && !data.session;

        if (data.user && data.session) {
          // Confirmed immediately (e.g. confirm email OFF) — set user
          setAuthUser(data.user as User);
          hadPreviousSessionRef.current = true;
          await loadAppProfile(data.user.id);
        }
        // If no session, do NOT set authUser — the user must verify first

        return { needsEmailConfirmation };
      } finally {
        setLoading(false);
      }
    },
    [appId, clearSessionExpired, loadAppProfile],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      sessionStorage.setItem('manual_sign_out', 'true');
      if (appId === 'kloutforce') {
        sessionStorage.removeItem('kloutforce:authReturnTo');
      }
    } catch (e) {
      // Storage may not be available
    }

    setLoading(true);
    await supabase.auth.signOut();
    clearTokens();
    setAuthUser(null);
    setAppUserId(null);
    setAppProfile(null);
    hadPreviousSessionRef.current = false;
    clearSessionExpired();
    setLoading(false);
  }, [appId, clearSessionExpired]);

  const linkAppUserFunc = useCallback(
    async (newAppUserId: string): Promise<void> => {
      if (!authUser) throw new Error('Not authenticated');
      await linkAuthUserToApp(authUser.id, appId, newAppUserId);
      setAppUserId(newAppUserId);
      setAppProfile(await getAppProfile(appId, newAppUserId));
    },
    [authUser, appId],
  );

  const updatePlatformLocale = useCallback(
    async (locale: SupportedPlatformLocale): Promise<void> => {
      if (!authUser) throw new Error('Not authenticated');
      const updatedUser = await updateAuthenticatedPlatformLocale(
        supabase.auth,
        authUser.user_metadata,
        locale,
      );
      setAuthUser(updatedUser as User);
    },
    [authUser],
  );

  const refreshAppProfile = useCallback(async (): Promise<void> => {
    if (!authUser) {
      console.log(`[AuthProvider:${appId}] refreshAppProfile - no user, skipping`);
      return;
    }
    console.log(`[AuthProvider:${appId}] Refreshing app profile for:`, authUser.email);
    // Force reload even if already loaded
    await loadAppProfile(authUser.id, true);
  }, [authUser, appId, loadAppProfile]);

  const value = useMemo<AuthState>(
    () => ({
      authUser,
      appUserId,
      appProfile,
      platformLocale,
      updatePlatformLocale,
      loading,
      authReady,
      isSessionExpired,
      sessionExpiredMessage,
      login,
      loginWithGoogle,
      loginWithMagicLink,
      requestEmailSignIn,
      signup,
      logout,
      linkAppUser: linkAppUserFunc,
      clearSessionExpired,
      refreshAppProfile,
    }),
    [
      authUser,
      appUserId,
      appProfile,
      platformLocale,
      updatePlatformLocale,
      loading,
      authReady,
      isSessionExpired,
      sessionExpiredMessage,
      login,
      loginWithGoogle,
      loginWithMagicLink,
      requestEmailSignIn,
      signup,
      logout,
      linkAppUserFunc,
      clearSessionExpired,
      refreshAppProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
